// src/components/admin/settings/DonationGoals.jsx
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Target, Plus, Edit2, Trash2, X, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import styles from '../../../styles/Settings.module.css';
import useTranslation from '../../../hooks/useTranslation';
import {
  selectDonationGoals,
  createDonationGoal,
  updateDonationGoal,
  deleteDonationGoal,
  selectSettingsUpdating,
} from '../../../store/slices/platformSettingsSlice';

const DonationGoals = () => {
  const dispatch = useDispatch();
  const donationGoalsRedux = useSelector(selectDonationGoals);
  const isSaving = useSelector(selectSettingsUpdating);

  const { translatedText: donationGoalsManagement } = useTranslation('Donation Goals Management');
  const { translatedText: textAddNewGoal } = useTranslation('Add New Goal');
  const { translatedText: active } = useTranslation('Active');
  const { translatedText: inactive } = useTranslation('Inactive');
  const { translatedText: deactivate } = useTranslation('Deactivate');
  const { translatedText: activate } = useTranslation('Activate');
  const { translatedText: duplicate } = useTranslation('Duplicate');
  const { translatedText: goalTitle } = useTranslation('Goal Title');
  const { translatedText: category } = useTranslation('Category');
  const { translatedText: description } = useTranslation('Description');
  const { translatedText: targetAmount } = useTranslation('Target Amount ($)');
  const { translatedText: currentAmount } = useTranslation('Current Amount ($)');
  const { translatedText: startDate } = useTranslation('Start Date');
  const { translatedText: endDate } = useTranslation('End Date');
  const { translatedText: currency } = useTranslation('Currency');
  const { translatedText: progress } = useTranslation('Progress');
  const { translatedText: noDonationGoals } = useTranslation('No Donation Goals');
  const { translatedText: addFirstGoal } = useTranslation('Start by adding your first donation goal.');
  const { translatedText: addYourFirstGoal } = useTranslation('Add Your First Goal');

  const [localGoals, setLocalGoals] = useState(donationGoalsRedux || []);
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [isEditingGoal, setIsEditingGoal] = useState(false);
  const [editingGoalId, setEditingGoalId] = useState(null);
  const [goalFormData, setGoalFormData] = useState({
    title: '',
    description: '',
    targetAmount: '',
    currentAmount: '',
    startDate: '',
    endDate: '',
    isActive: true,
    currency: 'USD',
    category: 'General'
  });

  React.useEffect(() => {
    if (donationGoalsRedux) {
      setLocalGoals(donationGoalsRedux);
    }
  }, [donationGoalsRedux]);

  const openCreateGoalModal = () => {
    setGoalFormData({
      title: '',
      description: '',
      targetAmount: '',
      currentAmount: '',
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      isActive: true,
      currency: 'USD',
      category: 'General'
    });
    setIsEditingGoal(false);
    setEditingGoalId(null);
    setShowGoalModal(true);
  };

  const openEditGoalModal = (goal) => {
    setGoalFormData({
      title: goal.title,
      description: goal.description || '',
      targetAmount: goal.targetAmount,
      currentAmount: goal.currentAmount || 0,
      startDate: goal.startDate,
      endDate: goal.endDate,
      isActive: goal.isActive,
      currency: goal.currency || 'USD',
      category: goal.category || 'General'
    });
    setIsEditingGoal(true);
    setEditingGoalId(goal.id || goal._id);
    setShowGoalModal(true);
  };

  const closeGoalModal = () => {
    setShowGoalModal(false);
    setGoalFormData({
      title: '',
      description: '',
      targetAmount: '',
      currentAmount: '',
      startDate: '',
      endDate: '',
      isActive: true,
      currency: 'USD',
      category: 'General'
    });
    setIsEditingGoal(false);
    setEditingGoalId(null);
  };

  const handleGoalFormChange = (field, value) => {
    setGoalFormData(prev => ({ ...prev, [field]: value }));
  };

  const submitGoalForm = async () => {
    if (!goalFormData.title.trim()) {
      toast.error('Please enter a goal title');
      return;
    }
    if (!goalFormData.targetAmount || goalFormData.targetAmount <= 0) {
      toast.error('Please enter a valid target amount');
      return;
    }
    if (!goalFormData.startDate) {
      toast.error('Please select a start date');
      return;
    }
    if (!goalFormData.endDate) {
      toast.error('Please select an end date');
      return;
    }
    if (new Date(goalFormData.startDate) > new Date(goalFormData.endDate)) {
      toast.error('Start date cannot be after end date');
      return;
    }

    try {
      if (isEditingGoal && editingGoalId !== null) {
        const index = localGoals.findIndex(g => (g.id || g._id) === editingGoalId);
        if (index !== -1) {
          const updatedGoal = {
            ...goalFormData,
            id: editingGoalId,
            targetAmount: Number(goalFormData.targetAmount),
            currentAmount: Number(goalFormData.currentAmount || 0)
          };
          await dispatch(updateDonationGoal({ index, updateData: updatedGoal })).unwrap();
          toast.success('Donation goal updated successfully!');
        }
      } else {
        const newGoal = {
          ...goalFormData,
          id: Date.now(),
          targetAmount: Number(goalFormData.targetAmount),
          currentAmount: Number(goalFormData.currentAmount || 0)
        };
        await dispatch(createDonationGoal(newGoal)).unwrap();
        toast.success('New donation goal added!');
      }
      closeGoalModal();
    } catch (error) {
      toast.error(error || 'Failed to save donation goal');
    }
  };

  const deleteGoal = async (goalId) => {
    if (window.confirm('Are you sure you want to delete this donation goal?')) {
      try {
        const index = localGoals.findIndex(g => (g.id || g._id) === goalId);
        if (index !== -1) {
          await dispatch(deleteDonationGoal(index)).unwrap();
          toast.success('Donation goal deleted!');
        }
      } catch (error) {
        toast.error(error || 'Failed to delete donation goal');
      }
    }
  };

  const toggleGoalStatus = async (goalId) => {
    const goal = localGoals.find(g => (g.id || g._id) === goalId);
    if (!goal) return;

    try {
      const index = localGoals.findIndex(g => (g.id || g._id) === goalId);
      const updatedGoal = { ...goal, isActive: !goal.isActive };
      await dispatch(updateDonationGoal({ index, updateData: updatedGoal })).unwrap();
      toast.success(goal.isActive ? 'Goal deactivated!' : 'Goal activated!');
    } catch (error) {
      toast.error(error || 'Failed to toggle goal status');
    }
  };

  const duplicateGoal = (goalId) => {
    const goalToDuplicate = localGoals.find(g => (g.id || g._id) === goalId);
    if (goalToDuplicate) {
      const newGoal = {
        ...goalToDuplicate,
        id: Date.now(),
        title: `${goalToDuplicate.title} (Copy)`,
        currentAmount: 0,
        isActive: true
      };
      dispatch(createDonationGoal(newGoal))
        .unwrap()
        .then(() => toast.success('Goal duplicated!'))
        .catch((error) => toast.error(error || 'Failed to duplicate goal'));
    }
  };

  const formatDateForInput = (dateValue) => {
    if (!dateValue) return '';
    try {
      const date = typeof dateValue === 'string' ? new Date(dateValue) : new Date(dateValue);
      if (isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0];
    } catch (e) {
      return '';
    }
  };

  return (
    <div className={styles.settingsCard}>
      {/* Modal */}
      {showGoalModal && (
        <div className={styles.modalOverlay} onClick={closeGoalModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>
                <Target size={20} />
                {isEditingGoal ? 'Edit Donation Goal' : 'Create New Donation Goal'}
              </h3>
              <button className={styles.modalCloseBtn} onClick={closeGoalModal}>
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label>{goalTitle || 'Goal Title'} *</label>
                <input
                  type="text"
                  value={goalFormData.title}
                  onChange={(e) => handleGoalFormChange('title', e.target.value)}
                  placeholder="Enter goal title"
                />
              </div>
              <div className={styles.formGroup}>
                <label>{category || 'Category'} *</label>
                <select
                  value={goalFormData.category}
                  onChange={(e) => handleGoalFormChange('category', e.target.value)}
                >
                  <option value="General">General</option>
                  <option value="Emergency Relief">Emergency Relief</option>
                  <option value="Education">Education</option>
                  <option value="Healthcare">Healthcare</option>
                  <option value="Infrastructure">Infrastructure</option>
                  <option value="Agriculture">Agriculture</option>
                  <option value="Youth Development">Youth Development</option>
                  <option value="Technology">Technology</option>
                  <option value="Women Empowerment">Women Empowerment</option>
                  <option value="Environment">Environment</option>
                </select>
              </div>
              <div className={styles.formGroup}>
                <label>{description || 'Description'}</label>
                <textarea
                  value={goalFormData.description}
                  onChange={(e) => handleGoalFormChange('description', e.target.value)}
                  rows={3}
                  placeholder="Describe the goal and its impact"
                />
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>{targetAmount || 'Target Amount ($)'} *</label>
                  <input
                    type="number"
                    value={goalFormData.targetAmount}
                    onChange={(e) => handleGoalFormChange('targetAmount', e.target.value)}
                    min="1"
                    placeholder="0"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>{currentAmount || 'Current Amount ($)'}</label>
                  <input
                    type="number"
                    value={goalFormData.currentAmount}
                    onChange={(e) => handleGoalFormChange('currentAmount', e.target.value)}
                    min="0"
                    placeholder="0"
                  />
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>{startDate || 'Start Date'} *</label>
                  <input
                    type="date"
                    value={goalFormData.startDate}
                    onChange={(e) => handleGoalFormChange('startDate', e.target.value)}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>{endDate || 'End Date'} *</label>
                  <input
                    type="date"
                    value={goalFormData.endDate}
                    onChange={(e) => handleGoalFormChange('endDate', e.target.value)}
                  />
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>{currency || 'Currency'}</label>
                  <select
                    value={goalFormData.currency}
                    onChange={(e) => handleGoalFormChange('currency', e.target.value)}
                  >
                    <option value="USD">USD - US Dollar</option>
                    <option value="EUR">EUR - Euro</option>
                    <option value="GBP">GBP - British Pound</option>
                    <option value="HTG">HTG - Haitian Gourde</option>
                    <option value="CAD">CAD - Canadian Dollar</option>
                  </select>
                </div>
                <div className={styles.formGroup}>
                  <label>Status</label>
                  <select
                    value={goalFormData.isActive ? 'active' : 'inactive'}
                    onChange={(e) => handleGoalFormChange('isActive', e.target.value === 'active')}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.modalCancelBtn} onClick={closeGoalModal}>Cancel</button>
              <button className={styles.modalSubmitBtn} onClick={submitGoalForm} disabled={isSaving}>
                {isSaving ? <RefreshCw size={16} className={styles.spinning} /> : (isEditingGoal ? 'Update Goal' : 'Create Goal')}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={styles.goalsHeader}>
        <h3>
          <Target size={20} />
          {donationGoalsManagement || 'Donation Goals Management'}
        </h3>
        <button className={styles.addGoalBtn} onClick={openCreateGoalModal} disabled={isSaving}>
          <Plus size={16} />
          {textAddNewGoal || 'Add New Goal'}
        </button>
      </div>

      <div className={styles.goalsList}>
        {localGoals.map((goal) => (
          <div key={goal.id || goal._id} className={styles.goalCard}>
            <div className={styles.goalHeader}>
              <div className={styles.goalTitleSection}>
                <h4>{goal.title}</h4>
                <span className={`${styles.goalStatus} ${goal.isActive ? styles.active : styles.inactive}`}>
                  {goal.isActive ? (active || 'Active') : (inactive || 'Inactive')}
                </span>
                <span className={styles.goalCategory}>{goal.category}</span>
              </div>
              <div className={styles.goalActions}>
                <button className={styles.editGoalBtn} onClick={() => openEditGoalModal(goal)} disabled={isSaving}>
                  <Edit2 size={14} />
                </button>
                <button className={styles.toggleGoalBtn} onClick={() => toggleGoalStatus(goal.id || goal._id)} disabled={isSaving}>
                  {goal.isActive ? (deactivate || 'Deactivate') : (activate || 'Activate')}
                </button>
                <button className={styles.duplicateGoalBtn} onClick={() => duplicateGoal(goal.id || goal._id)} disabled={isSaving}>
                  {duplicate || 'Duplicate'}
                </button>
                <button className={styles.deleteGoalBtn} onClick={() => deleteGoal(goal.id || goal._id)} disabled={isSaving}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            <div className={styles.goalForm}>
              <div className={styles.formGroup}>
                <label>{description || 'Description'}</label>
                <textarea
                  value={goal.description}
                  readOnly
                  rows={2}
                  className={styles.readOnlyTextarea}
                />
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>{targetAmount || 'Target Amount ($)'}</label>
                  <input type="text" value={`$${goal.targetAmount}`} readOnly className={styles.readOnlyInput} />
                </div>
                <div className={styles.formGroup}>
                  <label>{currentAmount || 'Current Amount ($)'}</label>
                  <input type="text" value={`$${goal.currentAmount || 0}`} readOnly className={styles.readOnlyInput} />
                </div>
                <div className={styles.formGroup}>
                  <label>{progress || 'Progress'}</label>
                  <div className={styles.progressDisplay}>
                    <div className={styles.progressBarSmall}>
                      <div 
                        className={styles.progressFillSmall} 
                        style={{ 
                          width: `${goal.targetAmount > 0 ? Math.min((goal.currentAmount / goal.targetAmount) * 100, 100) : 0}%` 
                        }}
                      />
                    </div>
                    <span className={styles.progressPercentage}>
                      {goal.targetAmount > 0 ? Math.round((goal.currentAmount / goal.targetAmount) * 100) : 0}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}

        {localGoals.length === 0 && (
          <div className={styles.emptyState}>
            <Target size={48} className={styles.emptyIcon} />
            <h4>{noDonationGoals || 'No Donation Goals'}</h4>
            <p>{addFirstGoal || 'Start by adding your first donation goal.'}</p>
            <button className={styles.addGoalBtn} onClick={openCreateGoalModal} disabled={isSaving}>
              <Plus size={16} />
              {addYourFirstGoal || 'Add Your First Goal'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DonationGoals;